import { useQuery } from "@tanstack/react-query";
import { vehicleDeliveryLocations } from "@fm/api/vehicle-delivery-locations";
import type { ApiClient } from "@fm/api/types";
import type { VehicleDeliveryLocation } from "@fm/api/vehicle-delivery-locations";
import { getApiBaseUrl } from "../lib/config";

/**
 * How long a locations request may take before callers stop waiting for it.
 * Pricing and booking wait for the locations of some vehicles, and
 * `isLoading` stays true for as long as a request never settles.
 */
export const DELIVERY_LOCATIONS_TIMEOUT_MS = 8000;

class DeliveryLocationsTimeout extends Error {
  constructor() {
    super("Delivery locations request timed out");
    this.name = "DeliveryLocationsTimeout";
  }
}

export interface UseDeliveryLocationsOptions {
  apiKey: string;
  vehicleId?: string;
  apiBaseUrl?: string;
  enabled?: boolean;
}

/**
 * Hook to fetch delivery locations for a specific vehicle
 *
 * @param options - Hook configuration options
 * @returns Query result with delivery locations
 *
 * @example
 * ```typescript
 * const { data, isLoading, error } = useDeliveryLocations({
 *   apiKey: 'pk_...',
 *   vehicleId: 'vehicle-456'
 * });
 * ```
 */
export function useDeliveryLocations(options: UseDeliveryLocationsOptions) {
  const { apiKey, vehicleId, apiBaseUrl, enabled = true } = options;

  const apiClient: ApiClient = {
    baseUrl: getApiBaseUrl(apiBaseUrl),
    apiKey,
  };

  return useQuery<VehicleDeliveryLocation[], Error>({
    queryKey: ["delivery-locations", vehicleId],
    queryFn: async () => {
      if (!vehicleId) {
        throw new Error("Vehicle ID is required");
      }
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const response = await Promise.race([
          vehicleDeliveryLocations.list(apiClient, vehicleId),
          new Promise<never>((_resolve, reject) => {
            timer = setTimeout(
              () => reject(new DeliveryLocationsTimeout()),
              DELIVERY_LOCATIONS_TIMEOUT_MS,
            );
          }),
        ]);
        return response.data;
      } finally {
        clearTimeout(timer);
      }
    },
    enabled: enabled && !!vehicleId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // `isLoading` stays true through every retry, and checkouts wait on it.
    // One retry for a failure; a request that hung is not retried (that
    // would double the wait).
    retry: (failureCount, error) =>
      !(error instanceof DeliveryLocationsTimeout) && failureCount < 1,
  });
}
